import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { URLParams, createUrl, request } from 'app/utils';
import { Flex, Loading, Page } from 'components';
import { useSchool } from 'features/school';
import { ProductDetailsApiResponse, ProductNextItems, ProductsContinuationApiResponse, useProducts } from 'features/products';
import { useDownloads } from 'features/downloads';
import {
  JourneyAreaCarousel,
  JourneyAreaWrapper,
  JourneyAreasHeader,
  JourneyCarousel,
  JourneyLearningChart,
  JourneyMetricChart,
  JourneyProductHero,
  JourneyRelationalMetricsModal,
  JourneyStartDateModal,
  JourneyVideoPlayer,
} from 'features/journeys';
import { useMenu } from 'features/menu';
import { ProductJourneyDashboardRelationalMetricResponse } from 'features/journeys/types/ProductJourneyDashboardRelationalMetricResponse';

const Wrapper = styled(Flex)(({ theme }) => {
  return `
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;
    background-color: ${theme.colors.dark_350};
    align-items: flex-start;
  `;
});

interface DetailsProps {
  enroll?: boolean;
}

const Details = ({ enroll }: DetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [showRelationalMetricModal, setShowRelationalMetricModal] = useState<{ relationalMetricUuid: string; period?: number }>(undefined!);
  const [productEnrolled, setProductEnrolled] = useState(false);

  const { productSlug, productClassUuid } = useParams();

  const { apiGatewayUrl } = useSchool();
  const { setBackPathname } = useMenu();
  const { productDetails, setProductDetails, clearProductDetails, setProductsContinuation, clearProductsContinuation } = useProducts();
  const { setDownloads } = useDownloads();
  const { schoolAlias } = useSchool();

  const [searchParams] = useSearchParams();
  const src = searchParams.get('src') || '';

  const fetchJourney = useCallback(
    async (showLoading = true, showEditLoading = false, forceEnroll = false) => {
      setLoading(showLoading);
      setEditLoading(showEditLoading);

      const params = URLParams({
        enroll: forceEnroll || enroll,
        sendEmail: (forceEnroll || enroll) && (schoolAlias !== 'b3'),
        src,
      });

      await request<ProductDetailsApiResponse>({
        url: `/api/v1/School/Products/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}&${params}` : `?${params}`}`,
        method: 'get',
        baseURL: apiGatewayUrl,
      })
        .then(async ({ data }) => {
          setProductDetails(data);
          setDownloads(data.downloads);

          if (data.enrolled) {
            setProductEnrolled(data.enrolled);
            await Promise.all([
              request<ProductsContinuationApiResponse>({
                url: `/api/v1/School/ProductContentNextItems/${productSlug}${productClassUuid ? `?productClassUuid=${productClassUuid}` : ``}`,
                method: 'get',
                baseURL: apiGatewayUrl,
              })
                .then(({ data }) => {
                  setProductsContinuation(data);
                })
                .catch(() => {}),
            ]).finally(() => {
              setLoading(false);
              setEditLoading(false);
            });
          } else {
            setLoading(false);
            setEditLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
          setEditLoading(false);
        });
    },
    [productSlug, productClassUuid, src]
  );

  const fetchJourneyMetric = useCallback(
    async (journeyMetricUuid: string, showLoading = true) => {
      setLoading(showLoading);

      await Promise.all([
        request<ProductJourneyDashboardRelationalMetricResponse>({
          url: `/api/v1/School/Products/JourneyMetric/${productSlug}/${journeyMetricUuid}`,
          method: 'get',
          baseURL: apiGatewayUrl,
        }).then(({ data }) => {
          setProductDetails({
            ...productDetails,
            journey: {
              ...productDetails?.journey,
              relationalMetrics: productDetails?.journey?.relationalMetrics.map((metric) => {
                if (journeyMetricUuid === metric.uuid) {
                  return data;
                }

                return metric;
              }),
            },
          });
        }),
      ]).finally(() => {
        setLoading(false);
      });
    },
    [productSlug, productDetails]
  );

  const clearPeriodValue = useCallback(
    (journeyMetricUuid: string, period: number) => {
      if (window.confirm('Você deseja excluir o valor?')) {
        request<{ apiResultData: string }>({
          method: 'PATCH',
          url: `/api/v1/School/AssessJourneyRelationalMetricPeriodValue/${productSlug}/${journeyMetricUuid}`,
          baseURL: apiGatewayUrl,
          params: {
            period,
          },
        }).finally(() => {
          fetchJourneyMetric(journeyMetricUuid, false);
        });
      }
    },
    [productSlug, productDetails]
  );

  const handleOpenRelationalMetricModal = (relationalMetricUuid: string, period = 0) => {
    setShowRelationalMetricModal({
      relationalMetricUuid,
      period,
    });
  };

  useEffect(() => {
    fetchJourney();
  }, [productSlug, productClassUuid, src]);

  useEffect(() => {
    return () => {
      clearProductDetails();
      clearProductsContinuation();

      setBackPathname(createUrl('journeys/details', [productSlug]));
    };
  }, []);

  const filteredAreas = useMemo(() => {
    return productDetails?.journey?.areas.filter((area) => !!area?.cards?.length);
  }, [productDetails?.journey]);

  if (loading) return <Loading label="Por favor, aguarde." />;

  return productDetails ? (
    <Page title={productDetails?.journey?.name}>
      {editLoading && <Loading alpha />}

      <JourneyVideoPlayer />

      {showStartDateModal && <JourneyStartDateModal onClose={() => setShowStartDateModal(false)} refetch={fetchJourney} />}

      {!!showRelationalMetricModal && (
        <JourneyRelationalMetricsModal
          onClose={() => setShowRelationalMetricModal(undefined!)}
          refetch={fetchJourneyMetric}
          relationalMetric={showRelationalMetricModal}
        />
      )}

      <Wrapper background={`url(${productDetails?.product?.coverImageUrl})`}>
        <Flex flexDirection="column" zIndex={5}>
          <JourneyProductHero
            hasNextItems={!!productDetails?.journey?.nextItems?.productName}
            openStartDateModal={() => setShowStartDateModal(true)}
            openRelationalMetricModal={handleOpenRelationalMetricModal}
            fetchJourney={(forceEnroll) => fetchJourney(false, true, forceEnroll)}
          />
        </Flex>
      </Wrapper>

      {!!productDetails?.journey && (
        <>
          {!!productDetails?.journey?.nextItems?.productName && <ProductNextItems nextItems={productDetails?.journey?.nextItems} />}

          {!!productDetails?.journey?.showJourneyAreasEvolution && <JourneyLearningChart />}

          {!!productDetails?.journey?.showRelationalMetrics &&
            !!productDetails?.journey?.relationalMetrics?.length &&
            productDetails?.journey?.relationalMetrics.map((metric) => (
              <JourneyMetricChart
                key={`metric-${metric.uuid}`}
                metric={metric}
                openRelationalMetricModal={handleOpenRelationalMetricModal}
                allowEditLatestRelationalMetricAssessment={productDetails?.journey?.allowEditLatestRelationalMetricAssessment}
                clearPeriodValue={(period: number) => clearPeriodValue(metric.uuid, period)}
              />
            ))}

          {!!productDetails?.journey?.liveMeetings?.length && (
            <JourneyCarousel
              cards={productDetails?.journey?.liveMeetings}
              name="Agenda de Lives"
              moreButtonLabel="Todas as Lives"
              moreButtonLink={createUrl('journeys/area/lives', [productSlug, productClassUuid])}
              productEnrolled={productEnrolled}
            />
          )}

          <Flex flexDirection="column">
            <JourneyAreasHeader />

            {filteredAreas?.map((area, areaIndex) => {
              return (
                <JourneyAreaWrapper key={`area-${areaIndex.toString()}`} area={area} isLast={areaIndex === filteredAreas?.length - 1}>
                  <JourneyAreaCarousel key={`journey-area-${area.uuid}`} area={area} productEnrolled={productEnrolled} />
                </JourneyAreaWrapper>
              );
            })}
          </Flex>
        </>
      )}
    </Page>
  ) : (
    <Loading label="Por favor, aguarde." />
  );
};

export default Details;
